import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type {
  CreateMembershipSkuFormData,
  UpdateMembershipSkuFormData,
} from "../../schemas/membershipSkuSchema";
import {
  createMembershipSkuSchema,
  updateMembershipSkuSchema,
} from "../../schemas/membershipSkuSchema";

interface MembershipSkuFormCreateProps {
  mode: "create";
  defaultValues?: Partial<CreateMembershipSkuFormData>;
  onSubmit: (data: CreateMembershipSkuFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  currentTenant?: string;
}

interface MembershipSkuFormEditProps {
  mode: "edit";
  defaultValues?: Partial<UpdateMembershipSkuFormData> & { isActive?: boolean };
  onSubmit: (data: UpdateMembershipSkuFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  currentTenant?: string;
}

type MembershipSkuFormProps =
  | MembershipSkuFormCreateProps
  | MembershipSkuFormEditProps;

export const MembershipSkuForm = ({
  mode,
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  currentTenant,
}: MembershipSkuFormProps) => {
  const schema =
    mode === "create" ? createMembershipSkuSchema : updateMembershipSkuSchema;

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues:
      mode === "create"
        ? {
            tenant: currentTenant || "",
            skuPattern: "",
            membershipDuration: undefined,
            description: "",
            ...defaultValues,
          }
        : {
            isActive: true,
            ...defaultValues,
          },
  });

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onSubmit={form.handleSubmit(onSubmit as never)}
      >
        <FormField
          control={form.control}
          name="skuPattern"
          render={({ field }) => (
            <FormItem>
              <FormLabel>SKU Pattern *</FormLabel>
              <FormControl>
                <Input placeholder="e.g., CLUB-ANNUAL" {...field} />
              </FormControl>
              <FormDescription>
                The SKU code that identifies this membership in QuickBooks
                invoices
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="membershipDuration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Membership Duration *</FormLabel>
              <Select defaultValue={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="annual">Annual (12 months)</SelectItem>
                  <SelectItem value="monthly">Monthly (30 days)</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                How long the membership is valid after invoice date
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Optional description"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {mode === "edit" && (
          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Active</FormLabel>
                  <FormDescription>
                    Enable or disable this SKU pattern
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <div className="flex justify-end gap-3">
          <Button
            disabled={isSubmitting}
            onClick={onCancel}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit">
            {isSubmitting
              ? "Saving..."
              : mode === "create"
                ? "Create"
                : "Update"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
